import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { HiHome, HiMenu, HiOutlineRefresh } from 'react-icons/hi';
import { RiMenuFold2Line, RiMenuUnfold2Line } from 'react-icons/ri';
import ProfileDropdown from '~/components/app/ProfileDropdown';
import UserSettingsModal from '~/components/app/UserSettingsModal';
import { ChatFAB, ChatPanel } from '~/components/ai-chat';
import { useAppState } from '~/stores/app-state';
import { useConnections, useConnectionStatus } from '~/hooks/useConnections';
import { useScopeStatus } from '~/hooks/useScopeStatus';
import { useLatestReport, useGenerateReport } from '~/hooks/useReport';
import { getRequestWithAuth } from '~/lib/api';

export default function AppLayout() {
  const { sidebarCollapsed, toggleSidebar, isChatOpen, toggleChat, closeChat } = useAppState();
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const queryClient = useQueryClient();

  // Keyboard shortcut: Cmd/Ctrl + K to toggle chat
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleChat();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleChat]);

  // Connection data from React Query (cached)
  const { data: connections } = useConnections();
  const shopify = connections?.find(
    (c) => c.connection_type === 'shopify_oauth' || c.connection_type === 'shopify'
  );

  // Chat only works with Shopify connections for now (Meta chat coming soon)
  const chatConnectionId = shopify?._id;

  // Check if re-authorization is needed for new scopes (only for OAuth connections, not API key)
  const isOAuthConnection = shopify?.connection_type === 'shopify_oauth';
  const { data: scopeStatus, isLoading: scopeLoading } = useScopeStatus(!!shopify && isOAuthConnection);
  const reauthInitiatedRef = useRef(false);
  const [reauthError, setReauthError] = useState<string | null>(null);

  useEffect(() => {
    if (scopeStatus?.needs_reauth && shopify && !reauthInitiatedRef.current) {
      reauthInitiatedRef.current = true;
      const initiateReauth = async () => {
        try {
          const { installUrl } = await getRequestWithAuth('/api/v1/shopify/installUrl');
          window.location.href = installUrl;
        } catch (error) {
          console.error('Failed to initiate re-authorization:', error);
          reauthInitiatedRef.current = false;
          setReauthError('Failed to update app permissions. Please refresh and try again.');
        }
      };
      initiateReauth();
    }
  }, [scopeStatus, shopify]);

  // Poll connection status if sync is in progress
  const shouldPollConnection = shopify?.sync_status === 'PENDING' || shopify?.sync_status === 'IN_SYNC';
  const { data: connectionStatus } = useConnectionStatus(
    shopify?._id ?? '',
    shouldPollConnection && !!shopify?._id
  );
  // Use polled status if available, otherwise fall back to cached
  const syncStatus = connectionStatus?.sync_status ?? shopify?.sync_status;
  const isSyncActive = syncStatus === 'PENDING' || syncStatus === 'IN_SYNC';
  const syncFailed = syncStatus === 'FAILED';

  // Report data - always fetch when connection exists (polling handles the wait)
  const { data: latestReport } = useLatestReport(shopify?._id, {
    enabled: !!shopify?._id,
  });
  const generateReportMutation = useGenerateReport();

  // Derive state
  const isReportGenerating =
    latestReport?.status === 'PENDING' || latestReport?.status === 'GENERATING';
  const hasCompletedReport = latestReport?.status === 'COMPLETED';

  const getStatusMessage = () => {
    if (syncFailed) return { text: 'Setup failed', isError: true };
    if (isReportGenerating) return { text: 'Generating...', isError: false };
    if (shopify && isSyncActive) return { text: 'Setting up...', isError: false };
    if (shopify && !hasCompletedReport) return { text: 'Setting up...', isError: false };
    return null;
  };

  const status = getStatusMessage();

  const handleRefresh = async () => {
    if (!shopify?._id) return;
    try {
      await generateReportMutation.mutateAsync({
        connectionId: shopify._id,
      });
      queryClient.invalidateQueries({ queryKey: ['latestReport', shopify._id] });
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  };

  // Show loading while checking scopes or redirecting for re-auth (OAuth only)
  if ((scopeLoading || scopeStatus?.needs_reauth) && shopify && isOAuthConnection && !reauthError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <span className="loading loading-spinner loading-lg" />
        {scopeStatus?.needs_reauth && (
          <p className="text-base-content/60">Updating app permissions...</p>
        )}
      </div>
    );
  }

  // Show error if re-auth redirect failed
  if (reauthError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="alert alert-error max-w-md">
          <span>{reauthError}</span>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <div className="drawer lg:drawer-open h-screen">
      <input
        id="sidebar"
        type="checkbox"
        className="drawer-toggle"
        checked={!sidebarCollapsed}
        onChange={toggleSidebar}
      />

      {/* Main content area */}
      <div className="drawer-content flex flex-col min-h-0">
        {/* Always-visible navbar */}
        <nav className="navbar w-full bg-base-300">
          <label htmlFor="sidebar" className="btn btn-square btn-ghost lg:hidden">
            <HiMenu className="text-xl" />
          </label>
          <div className="flex-1 px-4">{shopify?.name || 'Store Insights'}</div>
          {shopify && (
            <div className="flex items-center gap-2">
              {status && (
                <span className={`flex items-center gap-2 text-sm ${status.isError ? 'text-error' : 'text-base-content/60'}`}>
                  {!status.isError && <span className="loading loading-ring loading-sm" />}
                  {status.text}
                </span>
              )}
              {hasCompletedReport && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleRefresh}
                  disabled={generateReportMutation.isPending || isReportGenerating}
                >
                  <HiOutlineRefresh className="w-4 h-4" />
                  Refresh
                </button>
              )}
            </div>
          )}
        </nav>

        {/* Page content with chat panel */}
        <div className="flex flex-1 min-h-0">
          <main className="flex-1 overflow-auto p-4">
            <Outlet />
          </main>
          {chatConnectionId && (
            <ChatPanel
              connectionId={chatConnectionId}
              isOpen={isChatOpen}
              onClose={closeChat}
            />
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="drawer-side is-drawer-close:overflow-visible">
        <label htmlFor="sidebar" aria-label="close sidebar" className="drawer-overlay" />
        <aside className="flex min-h-full flex-col items-start bg-base-200 is-drawer-close:w-14 is-drawer-open:w-64">
          {/* Logo */}
          <div className="p-4 flex items-center justify-between w-full">
            <h1 className="is-drawer-close:hidden text-xl font-semibold text-base-content">
              Gamgee <span className="font-light opacity-60">Analytics</span>
            </h1>
            {/* Collapse toggle button */}
            <label
              htmlFor="sidebar"
              className="btn btn-ghost btn-sm btn-square cursor-pointer hidden lg:flex"
            >
              <RiMenuFold2Line className="is-drawer-open:hidden text-lg" />
              <RiMenuUnfold2Line className="is-drawer-close:hidden text-lg" />
            </label>
          </div>

          {/* Navigation */}
          <ul className="menu w-full grow">
            <li className="w-full">
              <NavLink
                to="/app/dashboard"
                className={({ isActive }) =>
                  `w-full is-drawer-close:tooltip is-drawer-close:tooltip-right is-drawer-close:justify-center ${isActive ? 'active' : ''}`
                }
                data-tip="Store Insights"
              >
                <HiHome className="text-lg" />
                <span className="is-drawer-close:hidden">Store Insights</span>
              </NavLink>
            </li>
          </ul>

          {/* Profile */}
          <div className="p-2 w-full">
            <div className="is-drawer-close:hidden">
              <ProfileDropdown collapsed={false} onOpenSettings={() => setShowSettingsModal(true)} />
            </div>
            <div className="is-drawer-open:hidden flex justify-center">
              <ProfileDropdown collapsed={true} onOpenSettings={() => setShowSettingsModal(true)} />
            </div>
          </div>
        </aside>
      </div>

      {/* Chat FAB - only show when report is ready (not during sync/loading) */}
      {chatConnectionId && hasCompletedReport && (
        <ChatFAB isOpen={isChatOpen} onClick={toggleChat} />
      )}

      <UserSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />
    </div>
  );
}
